import React, { useRef } from "react";
import ChipButton from "@/shared/components/ui/Button/ChipButton";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import CommonTextArea from "@/shared/components/ui/Input/CommonTextArea";
import FormField from "@/shared/components/ui/Form/FormField";
import InlineAlert from "@/shared/components/ui/Alert/InlineAlert";
import MessagePreview from "./MessagePreview";
import {
  getComposerCopy,
  getStaticContentLimit,
  TEMPLATE_VARIABLES,
} from "@/features/campaigns/utils/stepContentMode";

const SPINTAX_EXAMPLE = "{Hi|Hello|Hey}";

/**
 * Subject/message editor with variable + spintax insertion, validation and a live preview.
 * Shared by step fixed messages and campaign templates.
 */
const MessageComposer = ({
  channel,
  subject = "",
  onSubjectChange,
  body = "",
  onBodyChange,
  errors = [],
  previewLeads = [],
  sender,
}) => {
  const copy = getComposerCopy(channel);
  const limit = getStaticContentLimit(channel);

  // Inserts go where the user was last typing (subject or message), at the cursor.
  const lastField = useRef({ name: "body", start: null, end: null });
  const remember = (name) => (event) => {
    lastField.current = { name, start: event.target.selectionStart, end: event.target.selectionEnd };
  };
  const cursorProps = (name) => ({ onSelect: remember(name), onKeyUp: remember(name), onClick: remember(name) });

  const insert = (token) => {
    const { name, start, end } = lastField.current;
    const inSubject = name === "subject" && copy.hasSubject;
    const current = inSubject ? subject : body;
    const from = Number.isInteger(start) ? start : current.length;
    const to = Number.isInteger(end) ? end : from;
    const caret = from + token.length;
    lastField.current = { name: inSubject ? "subject" : "body", start: caret, end: caret };
    const next = current.slice(0, from) + token + current.slice(to);
    if (inSubject) onSubjectChange?.(next);
    else onBodyChange?.(next);
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="flex flex-col gap-3 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[0.7rem] text-gray-500 dark:text-gray-400">Insert:</span>
          {TEMPLATE_VARIABLES.map((variable) => (
            <ChipButton
              key={variable.key}
              keepFocus
              onClick={() => insert(`{{${variable.key}}}`)}
              tooltip={`Inserts {{${variable.key}}}, filled with each lead's value when sent.`}
            >
              {variable.label}
            </ChipButton>
          ))}
          <ChipButton
            keepFocus
            onClick={() => insert(SPINTAX_EXAMPLE)}
            tooltip="Spintax: each lead gets one of the options, always the same one for that lead. Varied wording helps deliverability."
          >
            Spintax
          </ChipButton>
        </div>

        {copy.hasSubject && (
          <FormField label={copy.subjectLabel}>
            <CommonInput
              placeholder={copy.subjectPlaceholder}
              type="text"
              value={subject}
              onChange={(e) => onSubjectChange?.(e.target.value)}
              {...cursorProps("subject")}
            />
          </FormField>
        )}

        <FormField
          label={copy.bodyLabel}
          hint={
            <>
              Fallback for missing values: {"{{company|your team}}"}. Vary wording per lead with {SPINTAX_EXAMPLE}.
            </>
          }
          meta={limit ? `${body.trim().length.toLocaleString()} / ${limit.toLocaleString()}` : null}
        >
          <CommonTextArea
            value={body}
            onChange={(e) => onBodyChange?.(e.target.value)}
            rows={10}
            placeholder={copy.bodyPlaceholder}
            textAreaClassName="resize-y"
            {...cursorProps("body")}
          />
        </FormField>

        {errors.length > 0 && (
          <InlineAlert variant="danger">
            <ul className="list-disc pl-4">
              {errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </InlineAlert>
        )}
      </div>

      <MessagePreview channel={channel} subject={subject} body={body} leads={previewLeads} sender={sender} />
    </div>
  );
};

export default MessageComposer;
